import React, { useState, useEffect, useRef, useCallback } from 'react';
import config from '../../config';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { Helmet } from 'react-helmet-async';

const Dashboard = () => {
    const [email, setEmail] = useState('');
    const [token] = useState(localStorage.getItem('token'));
    const [apiData, setApiData] = useState([]);
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filteredData, setFilteredData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const modalRef = useRef();

    // Authentication check
    useEffect(() => {
        if (!token) {
            navigate("/login");
        } else {
            setLoading(false);
        }
    }, [token, navigate]);

    // Fetch user data
    const fetchUserData = useCallback(async () => {
        try {
            const [emailResponse, dataResponse] = await Promise.all([
                fetch(`${config.baseUrl}/welcome`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                }),
                fetch(`${config.baseUrl}/registration/documents-user`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        contact_person_email: email,
                    }),
                })
            ]);

            if (emailResponse.ok) {
                const emailData = await emailResponse.json();
                setEmail(emailData.data.email);
            }

            if (dataResponse.ok) {
                const apiData = await dataResponse.json();
                setApiData(apiData.data);
                setFilteredData(apiData.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }, [token, email]);

    useEffect(() => {
        if (token) {
            fetchUserData();
        }
    }, [token, fetchUserData]);

    // Modal outside click handler
    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (showModal && modalRef.current && !modalRef.current.contains(event.target)) {
                setShowModal(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [showModal]);

    // Search and filter
    const filterData = useCallback((searchTerm = '') => {
        const lowercasedSearchTerm = searchTerm.toLowerCase();
        const filtered = apiData.filter((row) =>
            searchTerm === '' ||
            row.full_business_name?.toLowerCase().includes(lowercasedSearchTerm)
        );
        setFilteredData(filtered);
    }, [apiData]);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        filterData(e.target.value);
    };

    // Logout handler
    const handleLogout = async () => {
        try {
            await fetch(`${config.baseUrl}/logout`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });
            localStorage.removeItem('token');
            navigate('/login');
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    // View details handler
    const handleViewDetails = (item) => {
        setSelectedItem(item);
        setShowModal(true);
    };

    // Edit handler
    const handleEdit = async (id) => {
        try {
            const response = await fetch(`${config.baseUrl}/registration/get-form-1/${id}`);
            if (!response.ok) throw new Error('Failed to fetch form data');

            const { data } = await response.json();
            localStorage.removeItem('highestVisitedPage');
            localStorage.setItem('formOrderResult', JSON.stringify({ ...data, data: { id } }));

            const routes = {
                taxStrategyYesOrNo: '/tax-invite',
                businessBankingInterestedOrNot: '/small-banking',
                taxIdentificationSecurityNumber: '/process-ss4',
                taxIdentificationFirstName: '/process-ss4',
                rowInformationIndividualCompany: '/process-row',
                processMemberFirstName: '/process-member',
                processMemberCompanyName: '/process-member',
                companyAddressStreetAddress: '/process-company-address',
                contact_person_first_name: '/process-contact-person',
                business_name: '/process-coinfo',
                default: `/form-order?id=${id}`
            };

            const route = Object.entries(routes).find(([key]) => data[key])?.[1] || routes.default;
            navigate(route);
        } catch (error) {
            console.error('Edit error:', error);
            Swal.fire('Error', 'Failed to load form data', 'error');
        }
    };

    // Delete handler
    const handleDelete = async (id) => {
        if (!id) {
            Swal.fire('Error', 'Invalid booking ID!', 'error');
            return;
        }

        const confirmDelete = await Swal.fire({
            title: 'Are you sure?',
            text: 'This action cannot be undone!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
        });

        if (!confirmDelete.isConfirmed) return;

        try {
            const response = await fetch(`${config.baseUrl}/registration/delete/${id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) throw new Error('Booking Deletion Failed');

            Swal.fire('Deleted!', 'Booking Deleted Successfully.', 'success')
                .then(() => window.location.reload());
        } catch (error) {
            console.error('Delete error:', error);
            Swal.fire('Error!', error.message, 'error');
        }
    };

    if (loading) {
        return <div className="loading-spinner">Loading...</div>;
    }

    return (
        <>
            <Helmet>
                <title>Dashboard | Corpquick</title>
                <meta name="description" content="Corpquick Dashboard" />
            </Helmet>

            <div className="sidebar d-flex flex-column p-3">
                <Link to="/">
                    <div className="dashbord-logo text-center">
                        <img src="images/corpquick-final-Tranparen bg 1.png" alt="CorpQuick Logo" />
                    </div>
                </Link>
                <h4 className="mb-4 dashborad-heading">Dashboard</h4>
                <div className="dashboard-tab">
                    <nav className="nav nav-pills flex-column">
                        <a
                            className="nav-link"
                            id="v-home-tab"
                            data-bs-toggle="pill"
                            href="#v-home"
                            role="tab"
                            aria-controls="v-home"
                            aria-selected="true"
                        >
                            <i className="fa fa-home" aria-hidden="true"></i>
                            Bookings
                        </a>
                        <a className="nav-link" onClick={handleLogout}>
                            <i className="fa fa-sign-out" aria-hidden="true"></i>
                            Logout
                        </a>
                    </nav>
                </div>
            </div>

            <div className="content">
                <div className="detail-user">
                    <h4>Company Information</h4>
                </div>
                <input
                    type="text"
                    className="custom-input"
                    placeholder="Search by Business Name"
                    value={searchTerm}
                    onChange={handleSearch}
                />
                <div className="profile-table">
                    <table className='dashboard-table'>
                        <thead>
                            <tr>
                                <th>#ID</th>
                                <th>Business Name</th>
                                <th>Entity</th>
                                <th>State</th>
                                <th>Phone Number</th>
                                <th>Status</th>
                                <th>Action</th>
                                <th>Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.length > 0 ? (
                                filteredData.map((item, index) => (
                                    <TableRow
                                        key={item.id || index}
                                        item={item}
                                        index={index}
                                        onDelete={handleDelete}
                                        onEdit={handleEdit}
                                        onView={handleViewDetails}
                                    />
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="8">No Booking Found</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {showModal && selectedItem && (
                <Modal
                    selectedItem={selectedItem}
                    onClose={() => setShowModal(false)}
                    modalRef={modalRef}
                />
            )}
        </>
    );
};

// Extracted TableRow component for better performance
const TableRow = ({ item, index, onDelete, onEdit, onView }) => {
    const statusStyle = {
        backgroundColor: item.payment_status === null ? "lightyellow" : "lightgreen",
        color: item.payment_status === null ? "darkorange" : "green",
        padding: "8px",
        borderRadius: "5px",
    };

    return (
        <tr>
            <td>{index + 1}</td>
            <td>{item.full_business_name}</td>
            <td>{item.selected_entity}</td>
            <td>{item.selected_state}</td>
            <td>{item.contact_person_phone_number}</td>
            <td style={statusStyle}>
                {item.payment_status === null ? "Pending" : "Success"}
            </td>
            <td>
                {item.payment_status === null ? (
                    <div>
                        <button
                            type="button"
                            className="action"
                            onClick={() => onDelete(item.id)}
                        >
                            <i className="fa fa-trash" aria-hidden="true"></i>
                        </button>
                        <button
                            type="button"
                            className="action"
                            onClick={() => onEdit(item.id)}
                        >
                            <i className="fa fa-pencil" aria-hidden="true"></i>
                        </button>
                    </div>
                ) : "Contact Admin"}
            </td>
            <td>
                <button
                    type="button"
                    className="view-detail-pop"
                    onClick={() => onView(item)}
                >
                    View Details
                </button>
            </td>
        </tr>
    );
};

// Extracted Modal component for better performance
const Modal = ({ selectedItem, onClose, modalRef }) => {
    const parseAdditionalServices = () => {
        try {
            return typeof selectedItem.additional_services === "string"
                ? JSON.parse(selectedItem.additional_services)
                : selectedItem.additional_services || [];
        } catch (e) {
            console.error("Invalid JSON in additional_services", e);
            return [];
        }
    };

    const renderMemberRows = () => {
        if (!selectedItem.process_members?.length) return null;

        const memberData = selectedItem.process_members[0];
        const fields = [
            memberData.processMemberFirstName?.split(",") || [],
            memberData.processMemberLastName?.split(",") || [],
            memberData.processMemberStreetAddress?.split(",") || [],
            memberData.processMemberContAddress?.split(",") || [],
            memberData.processMemberCity?.split(",") || [],
            memberData.processMemberState?.split(",") || [],
            memberData.processMemberZipCode?.split(",") || []
        ];

        const maxLength = Math.max(...fields.map(f => f.length));

        return [...Array(maxLength)].map((_, index) => ([
            `${fields[0][index] || ""} ${fields[1][index] || ""}`,
            fields[2][index] || "",
            fields[3][index] || "",
            fields[4][index] || "",
            fields[5][index] || "",
            fields[6][index] || ""
        ]));
    };

    return (
        <div className='dashborad-modal'>
            <div className="modal show d-block" tabIndex="-1">
                <div className="modal-dialog modal-lg-custom" role="document" ref={modalRef}>
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">View Details</h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onClose}
                            ></button>
                        </div>
                        <div className="modal-body">
                            <DetailSection
                                title="Company Information"
                                content={
                                    <>
                                        <DetailTable rows={[
                                            { label: "Company name", value: selectedItem.business_name || '' },
                                            { label: "Package", value: selectedItem.package || '' },
                                            { label: "Entity", value: selectedItem.selected_entity || '' },
                                            { label: "State", value: selectedItem.selected_state || '' },
                                            { label: "State Fee", value: selectedItem.stateFee || '' },
                                            {
                                                label: "Additional Services",
                                                value: parseAdditionalServices().length > 0
                                                    ? parseAdditionalServices().map((service, i) => (
                                                        <div key={i}>{`${service.name.trim()}: $${service.price}`}</div>
                                                    ))
                                                    : "No additional services selected"
                                            },
                                            { label: "Total Price", value: selectedItem.form_total_price },
                                            {
                                                label: "Documents",
                                                value: selectedItem.documents ? (
                                                    <a
                                                        href={`${config.pdfUrl}/${selectedItem.documents}.pdf`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="document-link"
                                                    >
                                                        View Document
                                                    </a>
                                                ) : "No document available"
                                            }
                                        ]} />
                                    </>
                                }
                            />

                            <DetailSection
                                title="Contact Person"
                                content={
                                    <DetailTable
                                        header={["Full Name", "Email", "Phone Number"]}
                                        rows={selectedItem.contact_person_first_name ? [[
                                            `${selectedItem.contact_person_first_name || ''} ${selectedItem.contact_person_last_name || ''}`,
                                            selectedItem.contact_person_email || '',
                                            selectedItem.contact_person_phone_number || ''
                                        ]] : []}
                                    />
                                }
                            />

                            <DetailSection
                                title="Company Address Information"
                                content={
                                    <DetailTable
                                        header={["Company Address", "Street Address", "Address (Cont)", "City", "State", "Zip Code"]}
                                        rows={selectedItem.companyAddress ? [[
                                            selectedItem.companyAddress || '',
                                            selectedItem.companyAddressStreetAddress || '',
                                            selectedItem.companyAddressCount || '',
                                            selectedItem.companyAddressCity || '',
                                            selectedItem.companyAddressState || '',
                                            selectedItem.companyAddressZipCode || ''
                                        ]] : []}
                                    />
                                }
                            />

                            <DetailSection
                                title="Registered Agent Information"
                                content={
                                    <DetailTable
                                        header={["Agent Information Individual / Company", "Full Name", "Street Address", "Address (Cont)", "City", "State", "Zip Code"]}
                                        rows={selectedItem.agentInformationFirstName || selectedItem.agentInformationLastName ? [[
                                            selectedItem.registeredAgentInformation || '',
                                            `${selectedItem.agentInformationFirstName || ''} ${selectedItem.agentInformationLastName || ''}`,
                                            selectedItem.agentInformationStreet || '',
                                            selectedItem.agentInformationCont || '',
                                            selectedItem.agentInformationCity || '',
                                            selectedItem.agentInformationState || '',
                                            selectedItem.agentInformationZipcode || ''
                                        ]] : []}
                                    />
                                }
                            />

                            <DetailSection
                                title="Member Information"
                                content={
                                    <DetailTable
                                        header={["Full Name", "Street Address", "Address (Cont)", "City", "State", "Zip Code"]}
                                        rows={renderMemberRows()}
                                    />
                                }
                            />

                            <DetailSection
                                title="EIN / Tax Identification Number Information"
                                content={
                                    <DetailTable
                                        header={["Assign", "SSN/ITIN", "Full Name", "Street Address", "Address (Cont)", "City", "Country", "Zip Code"]}
                                        rows={
                                            selectedItem.taxIdentificationFirstName || selectedItem.taxIdentificationLastName ? [[
                                                selectedItem.registeredrowInformation || '',
                                                selectedItem.taxIdentificationSecurityNumber || '',
                                                `${selectedItem.taxIdentificationFirstName || ''} ${selectedItem.taxIdentificationLastName || ''}`.trim(),
                                                selectedItem.taxIdentificationStreetAddress || '',
                                                selectedItem.taxIdentificationAddressCont || '',
                                                selectedItem.taxIdentificationCity || '',
                                                selectedItem.taxIdentificationCountry || '',
                                                selectedItem.taxIdentificationZipCode || ''
                                            ]] : []
                                        }
                                    />
                                }
                            />

                            <DetailSection
                                title="Setting up Small Business Banking"
                                content={
                                    <DetailTable
                                        rows={[
                                            { label: "Interested / Ask Later", value: selectedItem.businessBankingInterestedOrNot }
                                        ]}
                                    />
                                }
                            />

                            <DetailSection
                                title="Tax Strategy / Free Consultation"
                                content={
                                    <DetailTable
                                        rows={[
                                            { label: "Yes / No", value: selectedItem.taxStrategyYesOrNot }
                                        ]}
                                    />
                                }
                            />
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onClose}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Reusable DetailSection component
const DetailSection = ({ title, content }) => (
    <div className='view-detailin'>
        <div className="company-info">{title}</div>
        <hr />
        <div className='table-container'>
            {content}
        </div>
    </div>
);

// Reusable DetailTable component
const DetailTable = ({ header, rows }) => {
    if (!rows || rows.length === 0) return (<div className='detail-table'>
        <table>
            {header && (
                <thead>
                    <tr>
                        {header.map((h, i) => (
                            <th key={i}><p>{h}</p></th>
                        ))}
                    </tr>
                </thead>
            )}
            <tbody>
                <tr>
                    <td colSpan={header ? header.length : 1}>No Data Available</td>
                </tr>
            </tbody>
        </table>
    </div>);

    return (
        <div className='detail-table'>
            <table>
                {header && (
                    <thead>
                        <tr>
                            {header.map((h, i) => (
                                <th key={i}><p>{h}</p></th>
                            ))}
                        </tr>
                    </thead>
                )}
                <tbody>
                    {Array.isArray(rows[0]) ? (
                        rows.map((row, i) => (
                            <tr key={i}>
                                {row.map((cell, j) => (
                                    <td key={j}>{cell}</td>
                                ))}
                            </tr>
                        ))
                    ) : (
                        rows.map((row, i) => (
                            <tr key={i}>
                                <th><p>{row.label}</p></th>
                                <td>{row.value}</td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default Dashboard;